
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Plus, Download, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from 'date-fns';
import { Banner } from '@/types/banner';
import { getAllBanners, downloadBanner, deleteBanner } from '@/utils/banner';
import { useAuth } from '@/context/SupabaseAuthContext';

const ManageBannersPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signOut } = useAuth();
  
  const [banners, setBanners] = useState<Banner[]>([]);
  const [filteredBanners, setFilteredBanners] = useState<Banner[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const loadBanners = () => {
      try {
        const allBanners = getAllBanners();
        // Sort by creation date (newest first)
        const sortedBanners = [...allBanners].sort(
          (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
        );
        setBanners(sortedBanners);
        setFilteredBanners(sortedBanners);
      } catch (error) {
        console.error('Error loading banners:', error);
        toast({
          title: "Error",
          description: "Failed to load banners. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadBanners();
  }, [toast]);
  
  // Filter banners when search query changes
  useEffect(() => {
    if (searchQuery) {
      const filtered = banners.filter(banner => 
        banner.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (banner.text && banner.text.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredBanners(filtered);
    } else {
      setFilteredBanners(banners);
    }
  }, [searchQuery, banners]);
  
  const handleDownload = async (banner: Banner) => {
    try {
      await downloadBanner(banner);
      toast({
        title: "Download started",
        description: `Banner #${banner.id} is being downloaded.`,
      });
    } catch (error) {
      console.error('Error downloading banner:', error);
      toast({
        title: "Download failed",
        description: "Failed to download the banner. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  const handleDelete = (bannerId: string) => {
    if (window.confirm('Are you sure you want to delete this banner?')) {
      try {
        const success = deleteBanner(bannerId);
        if (success) {
          setBanners(banners.filter(banner => banner.id !== bannerId));
          toast({
            title: "Banner deleted",
            description: `Banner #${bannerId} has been deleted successfully.`,
          });
        } else {
          toast({
            title: "Delete failed",
            description: "Banner not found or could not be deleted.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error('Error deleting banner:', error);
        toast({
          title: "Delete failed",
          description: "An error occurred while deleting the banner.",
          variant: "destructive",
        });
      }
    }
  };
  
  return (
    <AdminLayout isLoading={false} onLogout={signOut} userRole="admin">
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/marketing')}
              className="mb-2"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back to Marketing
            </Button>
            <h1 className="text-2xl font-bold">Manage Banners</h1>
            <p className="text-gray-500">
              View, download, and manage your created banner templates
            </p>
          </div>
          
          <Button
            onClick={() => navigate('/admin/marketing/create')}
            className="flex items-center"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create New Banner
          </Button>
        </div>
        
        <Separator className="mb-6" />
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-lg font-medium">All Banners</div>
              <div className="w-64">
                <Input
                  placeholder="Search banners..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            {isLoading ? (
              <div className="flex justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : filteredBanners.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-gray-500">No banners found</p>
                <p className="text-sm text-gray-400 mt-2">
                  {searchQuery ? 'Try a different search term' : 'Create your first banner to get started'}
                </p>
                {!searchQuery && (
                  <Button
                    variant="outline"
                    onClick={() => navigate('/admin/marketing/create')}
                    className="mt-4"
                  >
                    <Plus className="mr-1.5 h-4 w-4" />
                    Create a banner
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Banner ID</TableHead>
                      <TableHead>Preview</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Text</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBanners.map((banner) => (
                      <TableRow key={banner.id}>
                        <TableCell className="font-medium">
                          {banner.id}
                        </TableCell>
                        <TableCell>
                          <div 
                            className="w-16 h-16 bg-gray-100 border rounded overflow-hidden flex items-center justify-center"
                            style={{ backgroundColor: banner.backgroundColor }}
                          >
                            {banner.imageUrl ? (
                              <img 
                                src={banner.imageUrl} 
                                alt={`Banner ${banner.id}`} 
                                className="object-cover w-full h-full"
                              />
                            ) : (
                              <div className="text-xs text-center p-1" style={{ color: banner.backgroundColor === '#ffffff' ? '#000000' : '#ffffff' }}>
                                {banner.text.substring(0, 10)}{banner.text.length > 10 ? '...' : ''}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{banner.size}</TableCell>
                        <TableCell>
                          <div className="max-w-xs truncate">
                            {banner.text}
                          </div>
                        </TableCell>
                        <TableCell>
                          {format(banner.createdAt, 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownload(banner)}
                              title="Download banner"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(banner.id)}
                              className="text-red-500 hover:text-red-600 border-red-200 hover:border-red-300 hover:bg-red-50"
                              title="Delete banner"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default ManageBannersPage;
